// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * App orchestration: lobby → call. Owns app state, wires the DOM built in ui.ts
 * to the call controller, media, metering and the recorder. No heavy CPU work
 * here — a live call is inherently event-driven and main-thread.
 */
import './styles/main.css';
import QRCode from 'qrcode';
import { shellHtml, lobbyHtml, callHtml, makeTile, initials, escapeHtml } from './ui.ts';
import { icon } from './icons.ts';
import { initModals, openModal, closeModal, isModalOpen, toast, howItWorksHtml, privacyHtml, aboutHtml } from './modals.ts';
import { initGlossary } from './glossary.ts';
import { emit, mountEventDrawer } from './eventlog.ts';
import { CallController } from './call.ts';
import { getLocalStream, getDisplayStream, listDevices, stopStream, AudioMeter } from './media.ts';
import { CallRecorder, recorderSupported, fmtDur } from './recorder.ts';
import { deriveSasWords } from './crypto.ts';
import { generateRoomId, generatePassphrase, isValidRoomId, normalizePass } from './room.ts';
import { readInvite, writeRoomHash, currentInviteUrl } from './router.ts';
import type { Participant, Prefs, Presence } from './types.ts';

interface Tile {
  el: HTMLElement;
  video: HTMLVideoElement;
  meter?: AudioMeter;
}

const PREFS_KEY = 'meetwell:prefs';

const state = {
  mode: 'create' as 'create' | 'join',
  room: '',
  pass: '',
  includePass: true,
  prefs: loadPrefs(),
  localStream: null as MediaStream | null,
  camTrack: null as MediaStreamTrack | null,
  screenStream: null as MediaStream | null,
  sharing: false,
  viewOnly: false,
  controller: null as CallController | null,
  audioCtx: null as AudioContext | null,
  participants: new Map<string, Participant>(),
  tiles: new Map<string, Tile>(),
  recorder: null as CallRecorder | null,
  recording: false,
  recordStartedAt: 0,
  callStartedAt: 0,
  loop: null as ReturnType<typeof setInterval> | null,
  wakeLock: null as WakeLockSentinel | null,
  drawerDispose: null as (() => void) | null,
};

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return { micOn: true, camOn: true, name: '', ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return { name: '', micOn: true, camOn: true };
}
function savePrefs(): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(state.prefs));
  } catch {
    /* ignore */
  }
}

// ─── Boot ────────────────────────────────────────────────────────────────
function boot(): void {
  const app = document.getElementById('app')!;
  app.innerHTML = shellHtml();
  initModals();
  initGlossary(document.body);
  wireShell();

  const invite = readInvite();
  if (invite) {
    state.mode = 'join';
    state.room = invite.room;
    state.pass = invite.pass ?? '';
  } else {
    state.mode = 'create';
    state.room = generateRoomId();
    state.pass = generatePassphrase();
  }
  renderLobby();
  emit('system', 'ok', 'Meetwell ready — nothing has left your device', { mode: state.mode });
}

function wireShell(): void {
  document.querySelectorAll<HTMLElement>('[data-modal]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const which = btn.dataset.modal;
      if (which === 'how') openModal('How Meetwell works', howItWorksHtml());
      else if (which === 'privacy') openModal('Privacy & threat model', privacyHtml());
      else if (which === 'about') openModal('About Meetwell', aboutHtml());
    });
  });
  const logToggle = document.getElementById('log-toggle');
  logToggle?.addEventListener('click', toggleDrawer);
}

// ─── Event log drawer ───────────────────────────────────────────────────
function toggleDrawer(): void {
  const drawer = document.getElementById('drawer') as HTMLElement;
  if (drawer.hidden) {
    drawer.hidden = false;
    if (!state.drawerDispose) state.drawerDispose = mountEventDrawer(drawer);
    drawer.classList.add('open');
    drawer.querySelector<HTMLElement>('#drawer-close')?.addEventListener('click', closeDrawer, { once: true });
  } else {
    closeDrawer();
  }
}
function closeDrawer(): void {
  const drawer = document.getElementById('drawer') as HTMLElement;
  drawer.classList.remove('open');
  drawer.hidden = true;
}
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    const drawer = document.getElementById('drawer') as HTMLElement | null;
    if (drawer && !drawer.hidden && !isModalOpen()) closeDrawer();
  }
});

// ─── Lobby ───────────────────────────────────────────────────────────────
function renderLobby(): void {
  const view = document.getElementById('view')!;
  view.innerHTML = lobbyHtml({ mode: state.mode, room: state.room, name: state.prefs.name });

  const nameInput = document.getElementById('name-input') as HTMLInputElement;
  const preMic = document.getElementById('pre-mic') as HTMLButtonElement;
  const preCam = document.getElementById('pre-cam') as HTMLButtonElement;
  const joinBtn = document.getElementById('join-btn') as HTMLButtonElement;

  syncToggle(preMic, state.prefs.micOn, 'mic');
  syncToggle(preCam, state.prefs.camOn, 'video');

  nameInput.addEventListener('input', () => {
    state.prefs.name = nameInput.value.trim();
    updatePreviewAvatar();
  });
  preMic.addEventListener('click', () => {
    state.prefs.micOn = !state.prefs.micOn;
    syncToggle(preMic, state.prefs.micOn, 'mic');
    applyPreviewTracks();
  });
  preCam.addEventListener('click', () => {
    state.prefs.camOn = !state.prefs.camOn;
    syncToggle(preCam, state.prefs.camOn, 'video');
    applyPreviewTracks();
  });
  joinBtn.addEventListener('click', () => void enterCall());

  document.getElementById('join-instead')?.addEventListener('click', promptJoinCode);
  document.getElementById('start-instead')?.addEventListener('click', () => {
    state.mode = 'create';
    state.room = generateRoomId();
    state.pass = generatePassphrase();
    history.replaceState(null, '', location.pathname + location.search);
    renderLobby();
    void startPreview();
  });

  updatePreviewAvatar();
  void startPreview();
}

function syncToggle(btn: HTMLButtonElement, on: boolean, kind: 'mic' | 'video'): void {
  btn.setAttribute('aria-pressed', String(on));
  btn.classList.toggle('off', !on);
  const iconName = kind === 'mic' ? (on ? 'mic' : 'micOff') : on ? 'video' : 'videoOff';
  btn.innerHTML = icon(iconName);
}

function updatePreviewAvatar(): void {
  const av = document.getElementById('preview-avatar');
  if (av) av.textContent = initials(state.prefs.name || 'Guest');
}

async function startPreview(): Promise<void> {
  try {
    stopStream(state.localStream);
    state.localStream = await getLocalStream({
      camId: state.prefs.camId,
      micId: state.prefs.micId,
      video: state.prefs.camOn,
      audio: state.prefs.micOn,
    });
    state.viewOnly = false;
    state.camTrack = state.localStream.getVideoTracks()[0] ?? null;
    const video = document.getElementById('preview-video') as HTMLVideoElement | null;
    if (video) {
      video.srcObject = state.localStream;
      video.classList.add('mirror');
    }
    applyPreviewOff();
    await populateDevices();
    emit('media', 'ok', 'Camera + mic ready (local preview only)');
  } catch (err) {
    handlePermissionDenied(err as Error);
  }
}

async function populateDevices(): Promise<void> {
  try {
    const { cams, mics } = await listDevices();
    fillSelect('cam-select', cams, state.prefs.camId, 'Camera');
    fillSelect('mic-select', mics, state.prefs.micId, 'Microphone');
    document.getElementById('cam-select')?.addEventListener('change', (e) => {
      state.prefs.camId = (e.target as HTMLSelectElement).value || undefined;
      void startPreview();
    });
    document.getElementById('mic-select')?.addEventListener('change', (e) => {
      state.prefs.micId = (e.target as HTMLSelectElement).value || undefined;
      void startPreview();
    });
  } catch {
    /* enumerate can fail before permission; ignore */
  }
}

function fillSelect(id: string, devices: MediaDeviceInfo[], selected: string | undefined, kind: string): void {
  const sel = document.getElementById(id) as HTMLSelectElement | null;
  if (!sel) return;
  sel.innerHTML = `<option value="">Default ${kind.toLowerCase()}</option>`;
  devices.forEach((d, i) => {
    const opt = document.createElement('option');
    opt.value = d.deviceId;
    opt.textContent = d.label || `${kind} ${i + 1}`;
    if (d.deviceId === selected) opt.selected = true;
    sel.appendChild(opt);
  });
}

function applyPreviewTracks(): void {
  savePrefs();
  // Re-acquire only if we need a track we don't have; otherwise just enable/disable.
  const needVideo = state.prefs.camOn;
  const haveVideo = !!state.localStream?.getVideoTracks().length;
  const needAudio = state.prefs.micOn;
  const haveAudio = !!state.localStream?.getAudioTracks().length;
  if ((needVideo && !haveVideo) || (needAudio && !haveAudio)) {
    void startPreview();
    return;
  }
  state.localStream?.getVideoTracks().forEach((t) => (t.enabled = state.prefs.camOn));
  state.localStream?.getAudioTracks().forEach((t) => (t.enabled = state.prefs.micOn));
  applyPreviewOff();
}

function applyPreviewOff(): void {
  const off = document.getElementById('preview-off');
  if (off) off.hidden = state.prefs.camOn && !!state.localStream?.getVideoTracks().length;
}

function handlePermissionDenied(err: Error): void {
  state.viewOnly = true;
  state.localStream = null;
  const note = document.getElementById('perm-note');
  if (note) {
    note.hidden = false;
    note.innerHTML = `${escapeHtml(err.name === 'NotAllowedError' ? 'Camera/mic blocked.' : 'No camera/mic available.')} You can still join <strong>view-only</strong> to see and hear others.`;
  }
  const off = document.getElementById('preview-off');
  if (off) off.hidden = false;
  emit('media', 'warn', 'Camera/mic unavailable — view-only join offered', { error: err.name });
}

function promptJoinCode(): void {
  const code = window.prompt('Paste an invite link, or type a room code:');
  if (!code) return;
  const invite = readInviteFrom(code);
  if (!invite) {
    toast('That doesn’t look like a valid link or code', 'err');
    return;
  }
  state.mode = 'join';
  state.room = invite.room;
  state.pass = invite.pass ?? '';
  history.replaceState(null, '', `#room=${encodeURIComponent(invite.room)}${invite.pass ? `&pass=${encodeURIComponent(invite.pass)}` : ''}`);
  renderLobby();
  void startPreview();
}

function readInviteFrom(text: string): { room: string; pass?: string } | null {
  const t = text.trim();
  if (t.includes('#') || t.includes('room=')) {
    const invite = readInviteRaw(t);
    if (invite) return invite;
  }
  if (isValidRoomId(t)) return { room: t };
  return null;
}
function readInviteRaw(text: string): { room: string; pass?: string } | null {
  const hashIdx = text.indexOf('#');
  const hash = hashIdx >= 0 ? text.slice(hashIdx + 1) : text;
  const params = new URLSearchParams(hash);
  const room = params.get('room')?.trim();
  if (!room || !isValidRoomId(room)) return null;
  return { room, pass: params.get('pass')?.trim() || undefined };
}

// ─── Enter the call ──────────────────────────────────────────────────────
async function enterCall(): Promise<void> {
  const joinBtn = document.getElementById('join-btn') as HTMLButtonElement | null;
  if (joinBtn) joinBtn.disabled = true;
  savePrefs();

  // Ensure we have (or knowingly lack) a stream captured inside this tap.
  if (!state.localStream && !state.viewOnly) {
    try {
      state.localStream = await getLocalStream({
        camId: state.prefs.camId,
        micId: state.prefs.micId,
        video: state.prefs.camOn,
        audio: state.prefs.micOn,
      });
      state.camTrack = state.localStream.getVideoTracks()[0] ?? null;
    } catch {
      state.viewOnly = true;
    }
  }
  if (state.localStream) {
    state.localStream.getVideoTracks().forEach((t) => (t.enabled = state.prefs.camOn));
    state.localStream.getAudioTracks().forEach((t) => (t.enabled = state.prefs.micOn));
    state.camTrack = state.localStream.getVideoTracks()[0] ?? null;
  }

  try {
    state.audioCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    await state.audioCtx.resume().catch(() => undefined);
  } catch {
    state.audioCtx = null;
  }

  const presence = currentPresence();
  state.controller = new CallController(state.room, normalizePass(state.pass), presence, {
    onPeerJoin: (id) => onPeerJoin(id),
    onPeerLeave: (id) => onPeerLeave(id),
    onStream: (id, s) => onStream(id, s),
    onPresence: (id, p) => onPresence(id, p),
    onChat: (id, m) => onChat(id, m.text),
    onError: (msg) => {
      toast(msg, 'err');
      emit('signal', 'err', msg);
    },
  });

  renderCall();
  state.controller.join(state.localStream);
  state.callStartedAt = Date.now();
  writeRoomHash(state.room, state.pass, state.includePass);
  emit('signal', 'ok', 'Joined room — waiting for peers', { room: state.room });
  await deriveSas();
  startLoop();
  void acquireWakeLock();

  if (state.mode === 'create') setTimeout(() => openInviteModal(), 400);
}

function currentPresence(): Presence {
  return {
    name: state.prefs.name || 'Guest',
    micOn: state.prefs.micOn && !state.viewOnly,
    camOn: state.prefs.camOn && !state.viewOnly,
    sharing: state.sharing,
    recording: state.recording,
  };
}

async function deriveSas(): Promise<void> {
  try {
    const words = await deriveSasWords(normalizePass(state.pass), state.room);
    const bar = document.getElementById('sas-bar');
    const el = document.getElementById('sas-words');
    if (el) el.textContent = words.join(' · ');
    if (bar) bar.hidden = false;
    emit('crypto', 'ok', 'Verification words derived from passphrase', { words: words.join(' ') });
  } catch {
    emit('crypto', 'warn', 'Could not derive verification words');
  }
}

// ─── Call view ───────────────────────────────────────────────────────────
function renderCall(): void {
  const view = document.getElementById('view')!;
  view.innerHTML = callHtml();
  view.classList.add('in-call');

  // Self tile
  const selfTile: Tile = makeTile('self', state.prefs.name || 'Guest', true);
  document.getElementById('grid')!.appendChild(selfTile.el);
  state.tiles.set('self', selfTile);
  if (state.localStream) selfTile.video.srcObject = state.localStream;
  if (state.audioCtx && state.localStream) selfTile.meter = new AudioMeter(state.audioCtx, state.localStream);
  updateTileVisual('self');

  // Controls
  document.getElementById('btn-mic')!.addEventListener('click', toggleMic);
  document.getElementById('btn-cam')!.addEventListener('click', toggleCam);
  const screenBtn = document.getElementById('btn-screen') as HTMLButtonElement;
  if (!('getDisplayMedia' in navigator.mediaDevices)) screenBtn.hidden = true;
  else screenBtn.addEventListener('click', () => void toggleScreen());
  const recBtn = document.getElementById('btn-record') as HTMLButtonElement;
  if (!recorderSupported()) recBtn.hidden = true;
  else recBtn.addEventListener('click', () => void toggleRecord());
  document.getElementById('btn-leave')!.addEventListener('click', leaveCall);
  document.getElementById('invite-btn')!.addEventListener('click', openInviteModal);
  document.getElementById('btn-people')!.addEventListener('click', () => openSide('people'));
  document.getElementById('btn-chat')!.addEventListener('click', () => openSide('chat'));
  document.getElementById('side-close')!.addEventListener('click', closeSide);
  document.querySelectorAll<HTMLElement>('.side-tab[data-side]').forEach((t) =>
    t.addEventListener('click', () => openSide(t.dataset.side as 'people' | 'chat')),
  );
  document.getElementById('chat-form')!.addEventListener('submit', onChatSubmit);

  if (state.viewOnly) {
    (document.getElementById('btn-mic') as HTMLButtonElement).disabled = true;
    (document.getElementById('btn-cam') as HTMLButtonElement).disabled = true;
  }
  refreshLayout();
  refreshPeople();
}

// ─── Participants ────────────────────────────────────────────────────────
function ensureParticipant(id: string): Participant {
  let p = state.participants.get(id);
  if (!p) {
    p = { id, name: 'Guest', state: 'connecting', micOn: true, camOn: true, sharing: false, recording: false, level: 0, joinedAt: Date.now() };
    state.participants.set(id, p);
    const tile: Tile = makeTile(id, p.name, false);
    document.getElementById('grid')?.appendChild(tile.el);
    state.tiles.set(id, tile);
    refreshLayout();
  }
  return p;
}

function onPeerJoin(id: string): void {
  const p = ensureParticipant(id);
  p.state = 'connected';
  emit('peer', 'ok', 'Peer joined', { peer: short(id) });
  refreshPeople();
  refreshRecorderAudio();
}

function onPeerLeave(id: string): void {
  const p = state.participants.get(id);
  if (p) emit('peer', 'warn', `${p.name || 'A peer'} left`, { peer: short(id) });
  state.participants.delete(id);
  const tile = state.tiles.get(id);
  tile?.meter?.close();
  tile?.el.remove();
  state.tiles.delete(id);
  refreshLayout();
  refreshPeople();
  refreshRecorderAudio();
}

function onStream(id: string, stream: MediaStream): void {
  const p = ensureParticipant(id);
  p.stream = stream;
  const tile = state.tiles.get(id);
  if (tile) {
    tile.video.srcObject = stream;
    tile.video.play?.().catch(() => undefined);
    tile.meter?.close();
    if (state.audioCtx) tile.meter = new AudioMeter(state.audioCtx, stream);
  }
  emit('media', 'ok', 'Receiving media from peer', { peer: short(id) });
  updateTileVisual(id);
  refreshRecorderAudio();
}

function onPresence(id: string, presence: Presence): void {
  const p = ensureParticipant(id);
  p.name = presence.name || 'Guest';
  p.micOn = presence.micOn;
  p.camOn = presence.camOn;
  p.sharing = presence.sharing;
  p.recording = presence.recording;
  p.state = 'connected';
  updateTileVisual(id);
  refreshPeople();
}

function updateTileVisual(id: string): void {
  const tile = state.tiles.get(id);
  if (!tile) return;
  const self = id === 'self';
  const p = self ? null : state.participants.get(id);
  const camOn = self ? state.prefs.camOn && !state.viewOnly && !!state.localStream?.getVideoTracks().length : !!p?.camOn;
  const micOn = self ? state.prefs.micOn && !state.viewOnly : !!p?.micOn;
  const sharing = self ? state.sharing : !!p?.sharing;
  const recording = self ? state.recording : !!p?.recording;
  const name = self ? state.prefs.name || 'Guest' : p?.name || 'Guest';

  tile.el.classList.toggle('cam-off', !camOn && !sharing);
  tile.el.classList.toggle('sharing', sharing);
  const off = tile.el.querySelector('.tile-off') as HTMLElement;
  if (off) {
    off.hidden = camOn || sharing;
    (off.querySelector('.avatar') as HTMLElement).textContent = initials(name);
  }
  const micEl = tile.el.querySelector('.tile-mic') as HTMLElement;
  micEl.classList.toggle('muted', !micOn);
  micEl.innerHTML = icon(micOn ? 'mic' : 'micOff');
  const nameEl = tile.el.querySelector('.tile-name') as HTMLElement;
  nameEl.textContent = self ? `${name} (you)` : name;
  const rec = tile.el.querySelector('.tile-rec') as HTMLElement;
  rec.hidden = !recording;
}

function refreshLayout(): void {
  const grid = document.getElementById('grid');
  if (!grid) return;
  const n = state.tiles.size;
  grid.dataset.count = String(n);
}

function refreshPeople(): void {
  const body = document.getElementById('side-people');
  const count = document.getElementById('people-count');
  const total = state.participants.size + 1;
  if (count) count.textContent = String(total);
  if (!body) return;
  const rows: string[] = [];
  rows.push(personRow(`${state.prefs.name || 'Guest'} (you)`, state.prefs.micOn && !state.viewOnly, state.prefs.camOn && !state.viewOnly));
  for (const p of state.participants.values()) rows.push(personRow(p.name || 'Guest', p.micOn, p.camOn));
  body.innerHTML = rows.join('');
}

function personRow(name: string, micOn: boolean, camOn: boolean): string {
  return `<div class="person"><span class="avatar sm">${escapeHtml(initials(name))}</span><span class="person-name">${escapeHtml(name)}</span><span class="person-state">${micOn ? '' : 'muted'} ${camOn ? '' : 'cam off'}</span></div>`;
}

// ─── Controls ────────────────────────────────────────────────────────────
function toggleMic(): void {
  if (state.viewOnly || !state.localStream) return;
  state.prefs.micOn = !state.prefs.micOn;
  state.localStream.getAudioTracks().forEach((t) => (t.enabled = state.prefs.micOn));
  setCtl('btn-mic', state.prefs.micOn, 'mic', 'Mute', 'Unmute');
  updateTileVisual('self');
  broadcast();
  savePrefs();
  emit('media', 'info', state.prefs.micOn ? 'Microphone on' : 'Microphone muted');
}

function toggleCam(): void {
  if (state.viewOnly || !state.localStream) return;
  state.prefs.camOn = !state.prefs.camOn;
  state.localStream.getVideoTracks().forEach((t) => (t.enabled = state.prefs.camOn));
  setCtl('btn-cam', state.prefs.camOn, 'video', 'Camera', 'Camera');
  updateTileVisual('self');
  broadcast();
  savePrefs();
  emit('media', 'info', state.prefs.camOn ? 'Camera on' : 'Camera off');
}

async function toggleScreen(): Promise<void> {
  if (!state.controller) return;
  if (state.sharing) {
    stopScreen();
    return;
  }
  try {
    const screen = await getDisplayStream();
    state.screenStream = screen;
    state.sharing = true;
    const track = screen.getVideoTracks()[0];
    state.controller.replaceVideoTrack(track);
    const selfTile = state.tiles.get('self');
    if (selfTile) {
      selfTile.video.srcObject = screen;
      selfTile.video.classList.remove('mirror');
    }
    track.addEventListener('ended', stopScreen);
    setCtlActive('btn-screen', true);
    updateTileVisual('self');
    broadcast();
    emit('media', 'ok', 'Screen sharing started');
  } catch (err) {
    if ((err as Error).name !== 'NotAllowedError') toast('Could not start screen share', 'err');
  }
}

function stopScreen(): void {
  if (!state.sharing) return;
  stopStream(state.screenStream);
  state.screenStream = null;
  state.sharing = false;
  state.controller?.replaceVideoTrack(state.camTrack ?? null);
  const selfTile = state.tiles.get('self');
  if (selfTile) {
    selfTile.video.srcObject = state.localStream;
    if (state.localStream) selfTile.video.classList.add('mirror');
  }
  setCtlActive('btn-screen', false);
  updateTileVisual('self');
  broadcast();
  emit('media', 'info', 'Screen sharing stopped');
}

async function toggleRecord(): Promise<void> {
  if (!state.audioCtx) {
    toast('Recording needs audio support', 'err');
    return;
  }
  if (state.recording) {
    await stopRecording();
    return;
  }
  try {
    state.recorder = new CallRecorder(state.audioCtx, recorderSources);
    state.recordStartedAt = Date.now();
    state.recorder.start(state.recordStartedAt, recorderAudio());
    state.recording = true;
    setCtlActive('btn-record', true);
    updateTileVisual('self');
    broadcast();
    toast('Recording — saved to your device only', 'ok');
    emit('record', 'ok', 'Recording started (local only)');
  } catch (err) {
    toast('Could not start recording', 'err');
    emit('record', 'err', `Recording failed: ${(err as Error).message}`);
  }
}

async function stopRecording(): Promise<void> {
  if (!state.recorder) return;
  state.recording = false;
  setCtlActive('btn-record', false);
  updateTileVisual('self');
  broadcast();
  const blob = await state.recorder.stop();
  state.recorder = null;
  emit('record', 'ok', 'Recording finished', { size: `${(blob.size / 1048576).toFixed(1)} MB` });
  presentRecording(blob);
}

function recorderSources() {
  const out: { video: HTMLVideoElement | null; label: string; camOn: boolean }[] = [];
  const self = state.tiles.get('self');
  out.push({ video: self?.video ?? null, label: `${state.prefs.name || 'Guest'} (you)`, camOn: (state.prefs.camOn && !state.viewOnly && !!state.localStream?.getVideoTracks().length) || state.sharing });
  for (const [id, p] of state.participants) {
    const t = state.tiles.get(id);
    out.push({ video: t?.video ?? null, label: p.name || 'Guest', camOn: p.camOn || p.sharing });
  }
  return out;
}
function recorderAudio(): MediaStream[] {
  const streams: MediaStream[] = [];
  if (state.localStream && state.prefs.micOn && !state.viewOnly) streams.push(state.localStream);
  for (const p of state.participants.values()) if (p.stream) streams.push(p.stream);
  return streams;
}
function refreshRecorderAudio(): void {
  if (state.recording && state.recorder) state.recorder.syncAudio(recorderAudio());
}

function presentRecording(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
  const fname = `meetwell-call-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.${ext}`;
  const canShare = typeof navigator.share === 'function' && typeof navigator.canShare === 'function';
  openModal(
    'Your recording',
    `<p>Recorded entirely on your device — it was never uploaded.</p>
     <video class="rec-preview" controls src="${url}"></video>
     <div class="modal-actions">
       <a class="btn-primary" href="${url}" download="${escapeHtml(fname)}">Download .${ext}</a>
       ${canShare ? `<button type="button" class="btn-secondary" id="rec-share">Share…</button>` : ''}
     </div>
     <p class="muted small">${escapeHtml((blob.size / 1048576).toFixed(1))} MB · ${escapeHtml(blob.type || 'video/webm')}</p>`,
  );
  document.getElementById('rec-share')?.addEventListener('click', async () => {
    try {
      const file = new File([blob], fname, { type: blob.type || 'video/webm' });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: 'Meetwell recording' });
      else toast('Sharing not available here', 'err');
    } catch {
      /* cancelled */
    }
  });
}

function broadcast(): void {
  state.controller?.broadcastPresence(currentPresence());
}

function setCtl(id: string, on: boolean, kindOnFalseOff: 'mic' | 'video', labelOn: string, labelOff: string): void {
  const btn = document.getElementById(id) as HTMLButtonElement;
  btn.setAttribute('aria-pressed', String(on));
  btn.classList.toggle('active-off', !on);
  const iconName = kindOnFalseOff === 'mic' ? (on ? 'mic' : 'micOff') : on ? 'video' : 'videoOff';
  btn.innerHTML = `${icon(iconName)}<span>${on ? labelOn : labelOff}</span>`;
}
function setCtlActive(id: string, active: boolean): void {
  document.getElementById(id)?.classList.toggle('active', active);
}

// ─── Side panel + chat ───────────────────────────────────────────────────
function openSide(which: 'people' | 'chat'): void {
  const panel = document.getElementById('sidepanel') as HTMLElement;
  panel.hidden = false;
  document.querySelectorAll<HTMLElement>('.side-tab[data-side]').forEach((t) => t.classList.toggle('on', t.dataset.side === which));
  document.getElementById('side-people')!.hidden = which !== 'people';
  document.getElementById('side-chat')!.hidden = which !== 'chat';
  if (which === 'people') refreshPeople();
  if (which === 'chat') document.getElementById('chat-input')?.focus();
}
function closeSide(): void {
  (document.getElementById('sidepanel') as HTMLElement).hidden = true;
}

function onChatSubmit(e: Event): void {
  e.preventDefault();
  const input = document.getElementById('chat-input') as HTMLInputElement;
  const text = input.value.trim();
  if (!text) return;
  state.controller?.sendMessage(text);
  appendChat(state.prefs.name || 'You', text, true);
  input.value = '';
}
function onChat(id: string, text: string): void {
  const p = state.participants.get(id);
  appendChat(p?.name || 'Guest', text, false);
  if ((document.getElementById('sidepanel') as HTMLElement).hidden) toast('New message', 'info');
  emit('peer', 'info', 'Chat message received', { peer: short(id) });
}
function appendChat(name: string, text: string, mine: boolean): void {
  const log = document.getElementById('chat-log');
  if (!log) return;
  const row = document.createElement('div');
  row.className = `chat-msg${mine ? ' mine' : ''}`;
  row.innerHTML = `<span class="chat-name"></span><span class="chat-text"></span>`;
  (row.querySelector('.chat-name') as HTMLElement).textContent = mine ? 'You' : name;
  (row.querySelector('.chat-text') as HTMLElement).textContent = text;
  log.appendChild(row);
  log.scrollTop = log.scrollHeight;
}

// ─── Invite modal ────────────────────────────────────────────────────────
function openInviteModal(): void {
  const url = currentInviteUrl(state.room, state.pass, state.includePass);
  openModal(
    'Invite people',
    `<p class="muted small">Send this link. Whoever opens it joins your call — no account, no download.</p>
     <div class="invite-url"><input type="text" id="invite-input" readonly value="${escapeHtml(url)}" /><button type="button" class="icon-btn" id="invite-copy" title="Copy">${icon('copy')}</button></div>
     <div class="invite-actions">
       <button type="button" class="btn-secondary" id="invite-share">Share…</button>
       <button type="button" class="btn-secondary" id="invite-qr">Show QR</button>
     </div>
     <img id="invite-qr-img" class="qr-img" alt="QR code for the invite link" hidden />
     <label class="check-row"><input type="checkbox" id="invite-includepass" ${state.includePass ? 'checked' : ''} /> Include the passphrase in the link <span class="muted small">(convenient — anyone with the link can join). Uncheck for a link you can share publicly, then read the four words aloud instead.</span></label>`,
  );
  const input = document.getElementById('invite-input') as HTMLInputElement;
  document.getElementById('invite-copy')?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(input.value);
      toast('Invite link copied', 'ok');
    } catch {
      input.select();
      document.execCommand('copy');
      toast('Invite link copied', 'ok');
    }
  });
  document.getElementById('invite-share')?.addEventListener('click', async () => {
    try {
      if (typeof navigator.share === 'function') await navigator.share({ title: 'Join my Meetwell call', url: input.value });
      else {
        await navigator.clipboard.writeText(input.value);
        toast('Link copied', 'ok');
      }
    } catch {
      /* cancelled */
    }
  });
  document.getElementById('invite-qr')?.addEventListener('click', async () => {
    const img = document.getElementById('invite-qr-img') as HTMLImageElement;
    if (!img.hidden) {
      img.hidden = true;
      return;
    }
    try {
      img.src = await QRCode.toDataURL(input.value, { margin: 1, width: 240, color: { dark: '#0a0f14', light: '#ffffff' } });
      img.hidden = false;
    } catch {
      toast('Could not render QR', 'err');
    }
  });
  document.getElementById('invite-includepass')?.addEventListener('change', (e) => {
    state.includePass = (e.target as HTMLInputElement).checked;
    input.value = currentInviteUrl(state.room, state.pass, state.includePass);
    writeRoomHash(state.room, state.pass, state.includePass);
    const img = document.getElementById('invite-qr-img') as HTMLImageElement;
    img.hidden = true;
  });
}

// ─── Loop, wake lock, teardown ───────────────────────────────────────────
function startLoop(): void {
  if (state.loop) clearInterval(state.loop);
  state.loop = setInterval(() => {
    for (const [id, tile] of state.tiles) {
      const lvl = tile.meter?.read() ?? 0;
      const p = id === 'self' ? null : state.participants.get(id);
      const micOn = id === 'self' ? state.prefs.micOn && !state.viewOnly : !!p?.micOn;
      tile.el.style.setProperty('--level', String(micOn ? lvl : 0));
      tile.el.classList.toggle('speaking', micOn && lvl > 0.18);
    }
    const timer = document.getElementById('call-timer');
    if (timer && state.callStartedAt) timer.textContent = fmtDur(Math.floor((Date.now() - state.callStartedAt) / 1000));
  }, 100);
}

async function acquireWakeLock(): Promise<void> {
  try {
    if ('wakeLock' in navigator) state.wakeLock = await navigator.wakeLock.request('screen');
  } catch {
    /* not critical */
  }
}
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && state.callStartedAt && !state.wakeLock) void acquireWakeLock();
});

function leaveCall(): void {
  emit('system', 'info', 'Leaving call');
  if (state.recording) void stopRecording();
  if (state.loop) clearInterval(state.loop);
  state.loop = null;
  for (const tile of state.tiles.values()) tile.meter?.close();
  state.tiles.clear();
  state.controller?.leave();
  state.controller = null;
  stopScreen();
  stopStream(state.localStream);
  state.localStream = null;
  state.participants.clear();
  try {
    state.wakeLock?.release();
  } catch {
    /* ignore */
  }
  state.wakeLock = null;
  try {
    void state.audioCtx?.close();
  } catch {
    /* ignore */
  }
  state.audioCtx = null;
  state.callStartedAt = 0;
  closeModal();
  const view = document.getElementById('view')!;
  view.classList.remove('in-call');
  // Reset to a fresh lobby to start again.
  state.mode = 'create';
  state.room = generateRoomId();
  state.pass = generatePassphrase();
  state.sharing = false;
  state.viewOnly = false;
  history.replaceState(null, '', location.pathname + location.search);
  renderLobby();
  toast('You left the call', 'info');
}

function short(id: string): string {
  return id.slice(0, 6);
}

window.addEventListener('pagehide', () => {
  state.controller?.leave();
  stopStream(state.localStream);
  stopStream(state.screenStream);
});

boot();
